import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faMagnifyingGlass,
} from '@fortawesome/sharp-solid-svg-icons';
import { createContext, useContext, useMemo, useState } from 'react';
import { SCInputString } from '../inputs';
import { SCView } from '../view';
import { PROPERTY_NODE_TYPES } from './core';

interface SCCodeFragmentProps {
  disabled?: boolean;
  language: keyof typeof PROPERTY_NODE_TYPES;
  /**
   * Optional alphabetical sort of each group's children (by `label`). Group
   * order is preserved — only the items within a group are reordered.
   * Defaults to 'None' (caller-provided order).
   */
  sort?: 'Asc' | 'Desc' | 'None';
  /** Testid for the always-on search box (kept optional so consumers can namespace it). */
  dataTestId?: string;
  items: {
    label: string;
    children: {
      label: string;
      value: string;
      info?: string;
    }[];
  }[];
}

const CodeFragmentContext = createContext<{
  disabled?: boolean;
}>({});

function Item({ item, language }: { item: any; language: keyof typeof PROPERTY_NODE_TYPES }) {
  const { disabled } = useContext(CodeFragmentContext);
  return (
    <SCView py={2} px={3} className="sce-fragment-item" title={item.info || item.value}>
      <SCView
        cursorGrab={!disabled}
        draggable={!disabled}
        title={item.label}
        truncate
        onDragStart={(e) => {
          if (disabled) return; // not allowed
          // @ts-ignore
          e.dataTransfer.setData(
            'text/plain',
            `${PROPERTY_NODE_TYPES[language].accessSymbols}${item.value}`,
          );
        }}
      >
        {item.label}
      </SCView>
    </SCView>
  );
}
function Group({
  item,
  language,
  forceOpen,
}: {
  item: any;
  language: keyof typeof PROPERTY_NODE_TYPES;
  forceOpen?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // While a search is active every matching group is forced open so results are visible; otherwise the
  // group uses its own collapse state.
  const isOpen = forceOpen || open;

  return (
    <div>
      <SCView
        className="sce-fragment-group"
        py={2}
        px={3}
        borderB
        borderT
        flex
        itemsCenter
        justifyBetween
        cursorPointer
        mt={'-1px'}
        selectNone
        sticky
        top={'-1px'}
        style={{ backgroundColor: 'var(--surface-base-light)' }}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <SCView textLink fontBold>
          {item.label}
        </SCView>
        <SCView w={'16px'} textDesc>
          <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />
        </SCView>
      </SCView>
      {isOpen && (
        <div>
          {item.children.map((child, index) => (
            <Item key={index} item={child} language={language} />
          ))}
        </div>
      )}
    </div>
  );
}

function SCCodeFragment({
  disabled,
  language,
  items,
  sort = 'None',
  dataTestId,
}: SCCodeFragmentProps) {
  const [query, setQuery] = useState('');

  // Sort each group's children by label without mutating the caller's arrays or
  // reordering the groups themselves. 'None' returns items untouched.
  const sortedItems = useMemo(() => {
    if (sort === 'None') return items;
    const dir = sort === 'Desc' ? -1 : 1;
    return items.map((group) => ({
      ...group,
      children: [...group.children].sort(
        // Case-insensitive (sensitivity: 'base') so lowercase labels interleave
        // with uppercase ones — same collation as the additional-columns picker.
        (a, b) => dir * a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      ),
    }));
  }, [items, sort]);

  // Filter each group's children by the search text (matching label or value); while searching, groups
  // with no matches are dropped. Empty query leaves the list untouched.
  const q = query.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (!q) return sortedItems;
    return sortedItems
      .map((group) => ({
        ...group,
        children: group.children.filter(
          (c) => c.label.toLowerCase().includes(q) || (c.value ?? '').toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.children.length > 0);
  }, [sortedItems, q]);

  return (
    <CodeFragmentContext.Provider value={{ disabled }}>
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: 'var(--surface-base-light)',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            padding: 4,
            backgroundColor: 'var(--surface-base-light)',
          }}
        >
          <SCInputString
            Value={query}
            OnChange={setQuery}
            Placeholder="Search…"
            Icon={faMagnifyingGlass}
            DataTestId={dataTestId}
          />
        </div>
        {filteredItems.map((item, index) => (
          <Group key={index} item={item} language={language} forceOpen={q.length > 0} />
        ))}
      </div>
    </CodeFragmentContext.Provider>
  );
}

export { SCCodeFragment };
export type { SCCodeFragmentProps };

// note - daniel creating a slightly altered version of the above for selecting placeholders in generic rest query data source
function VariableItem({
  item,
  onClick,
  onDoubleClick,
  selected,
}: {
  item: any;
  onClick: any;
  onDoubleClick?: any;
  selected?: boolean;
}) {
  return (
    <SCView
      py={2}
      px={3}
      hover={{
        bgGray100: true,
      }}
      style={selected ? { backgroundColor: '#cfe8ff' } : undefined}
      title={item.info || item.value}
    >
      <SCView draggable title={item.label} truncate onClick={onClick} onDoubleClick={onDoubleClick}>
        {item.label}
      </SCView>
    </SCView>
  );
}
function VariableGroup({
  item,
  onClick,
  onDoubleClick,
  selectedValue,
}: {
  item: any;
  onClick: any;
  onDoubleClick?: (label: string) => void;
  selectedValue?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <SCView
        py={2}
        px={3}
        borderB
        borderT
        bgGray300
        flex
        itemsCenter
        justifyBetween
        cursorPointer
        mt={'-1px'}
        selectNone
        sticky
        top={'-1px'}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <SCView textLink>{item.label}</SCView>
        <SCView w={'16px'} textDesc>
          <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
        </SCView>
      </SCView>
      {open && (
        <div>
          {item.children.map((child, index) => (
            <VariableItem
              key={index}
              item={child}
              onClick={() => onClick(child.value)}
              onDoubleClick={onDoubleClick ? () => onDoubleClick(child.value) : undefined}
              selected={selectedValue === child.value}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface SCSelectVariableProps {
  onClick: (value: string) => void;
  onDoubleClick?: (value: string) => void;
  selectedValue?: string;
  items: {
    label: string;
    children: {
      label: string;
      value: string;
    }[];
  }[];
}

function SCSelectVariable({ items, onClick, onDoubleClick, selectedValue }: SCSelectVariableProps) {
  return (
    <SCView wFull hFull border overflowYAuto>
      {items.map((item, index) => (
        <VariableGroup
          key={index}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          selectedValue={selectedValue}
          item={item}
        />
      ))}
    </SCView>
  );
}

export { SCSelectVariable };
export type { SCSelectVariableProps };
